import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { makeStyles, SwipeableDrawer } from '@material-ui/core';
import shadows from "../../foundations/shadows.module.scss";
import { ChevronLeft } from "../../icons";
import { useStateInRef } from "../../utils/useStateInRef";
import { Button } from "../Button";
import { CloseButton } from "../CloseButton/CloseButton";
import { IconButton } from "../IconButton";
import { useOverlayContext } from "../OverlayContext";
import { Typography } from "../Typography";
import styles from "./BottomSheet.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const HeaderContext = /*#__PURE__*/React.createContext(null);
const FooterContext = /*#__PURE__*/React.createContext(null);

/** Renders inside the BottomSheet header slot. */
export function BottomSheetHeader(props) {
  const {
    title,
    subtitle,
    paddingBottom
  } = props;
  const element = useContext(HeaderContext);
  const jsx = /*#__PURE__*/_jsxs("div", {
    className: styles.header,
    "data-testid": "bottom-sheet-header",
    style: {
      paddingBottom
    },
    children: [/*#__PURE__*/_jsx(Typography, {
      variant: "h6",
      weight: "normal",
      children: title
    }), /*#__PURE__*/_jsx(Typography, {
      skin: "secondary",
      children: subtitle
    })]
  });
  return element ? /*#__PURE__*/createPortal(jsx, element) : jsx;
}

/** Renders inside the BottomSheet footer slot. */
export function BottomSheetFooter(props) {
  var _primaryAction$testId, _secondaryAction$test;
  const {
    primaryAction,
    secondaryAction,
    paddingGutter = 8,
    paddingTop = 8
  } = props;
  const element = useContext(FooterContext);
  const jsx = /*#__PURE__*/_jsxs("div", {
    "data-testid": "bottom-sheet-footer",
    className: styles.footer,
    style: {
      paddingLeft: paddingGutter,
      paddingRight: paddingGutter,
      paddingTop
    },
    children: [primaryAction && /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Button, {
        fluid: true,
        roundness: "m",
        onClick: primaryAction.onClick,
        disabled: primaryAction.disabled,
        "data-testid": (_primaryAction$testId = primaryAction.testId) !== null && _primaryAction$testId !== void 0 ? _primaryAction$testId : 'bottom-sheet-primary-action',
        children: primaryAction.text
      })
    }), secondaryAction && /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Button, {
        fluid: true,
        roundness: "m",
        skin: "primaryOutline",
        onClick: secondaryAction.onClick,
        disabled: secondaryAction.disabled,
        "data-testid": (_secondaryAction$test = secondaryAction.testId) !== null && _secondaryAction$test !== void 0 ? _secondaryAction$test : 'bottom-sheet-secondary-action',
        children: secondaryAction.text
      })
    })]
  });
  return element ? /*#__PURE__*/createPortal(jsx, element) : jsx;
}
const maxHeight = 'calc(100% - 48px)';
export const useBottomSheetStyles = makeStyles({
  paper: {
    borderRadius: '16px 16px 0 0',
    boxShadow: shadows.sh60,
    display: 'flex',
    flexDirection: 'column',
    maxHeight,
    height: props => props.maxHeight ? maxHeight : 'auto'
  },
  backdrop: {
    backgroundColor: "rgba(0,0,0,0.2)"
  }
});
export function BottomSheet(props) {
  const {
    open,
    onClose,
    onOpen = () => {},
    children,
    showBackButton,
    maxHeight,
    onBack,
    onExited,
    paddingBottom = 16
  } = props;
  const getProps = useStateInRef(props);
  const {
    isTestMode
  } = useOverlayContext();
  const contentRef = useRef(null);
  const [headerEl, setHeaderEl] = useState(null);
  const [footerEl, setFooterEl] = useState(null);
  const classes = useBottomSheetStyles({
    maxHeight
  });
  useEffect(() => {
    const disableSwipeIfInContent = e => {
      if (getProps().open && contentRef.current && e.target && contentRef.current.contains(e.target)) {
        // This tells MUI SwipeableDrawer to not handle the swipe.
        e['muiHandled'] = true;
      }
    };
    document.addEventListener('touchstart', disableSwipeIfInContent, true);
    return () => {
      document.removeEventListener('touchstart', disableSwipeIfInContent);
    };
  }, []);
  return /*#__PURE__*/_jsx(SwipeableDrawer, {
    disableDiscovery: true,
    disableSwipeToOpen: true,
    classes: {
      paper: classes.paper
    },
    anchor: "bottom",
    open: open,
    onClose: onClose,
    onOpen: onOpen,
    transitionDuration: isTestMode ? 0 : undefined,
    SlideProps: {
      onExited
    },
    ModalProps: {
      BackdropProps: {
        classes: {
          root: classes.backdrop
        }
      }
    },
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.root,
      style: {
        paddingBottom
      },
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.top,
        children: [showBackButton && /*#__PURE__*/_jsx("div", {
          className: styles.backButton,
          children: /*#__PURE__*/_jsx(IconButton, {
            noPadding: true,
            onClick: onBack,
            size: "small",
            "data-testid": "bottom-sheet-back-button",
            children: /*#__PURE__*/_jsx(ChevronLeft, {})
          })
        }), /*#__PURE__*/_jsx("div", {
          className: styles.swipeIndicator
        }), /*#__PURE__*/_jsx(CloseButton, {
          noPadding: true,
          className: styles.closeButton,
          size: "small",
          onClick: onClose,
          "data-testid": "bottom-sheet-close-button"
        })]
      }), /*#__PURE__*/_jsx("div", {
        "data-testid": "bottom-sheet-header-slot",
        ref: setHeaderEl
      }), /*#__PURE__*/_jsx("div", {
        className: styles.content,
        ref: contentRef,
        children: /*#__PURE__*/_jsx(HeaderContext.Provider, {
          value: headerEl,
          children: /*#__PURE__*/_jsx(FooterContext.Provider, {
            value: footerEl,
            children: children
          })
        })
      }), /*#__PURE__*/_jsx("div", {
        "data-testid": "bottom-sheet-footer-slot",
        ref: setFooterEl
      })]
    })
  });
}